import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OpenAccountDetailDialog } from '@/modules/open-accounts/OpenAccountDetailDialog';
import { OPEN_ACCOUNTS_QUERY } from '@/modules/open-accounts/open-accounts.gql';
import { OPEN_ACCOUNT_KIND_TO_GQL, OPEN_ACCOUNT_STATUS_TO_GQL } from '@/shared/graphql/enums';
import { formatCentsString } from '@/shared/lib/money';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';

interface OpenAccountListItem {
  id: string;
  folio_number: string | null;
  balance_cents: string;
  tutor_name: string | null;
  pet_name: string | null;
}

interface OpenAccountsResponse {
  openAccounts: { items: OpenAccountListItem[] };
}

interface HospitalizationLookupModalProps {
  on_close: () => void;
}

/**
 * The single-station equivalent of `CashierPosPage.tsx`'s embedded "Hospitalizaciones"
 * panel — same query/filter (open hospitalization_1/2 accounts), moved behind a button.
 * Read/manage-only: opens the existing `OpenAccountDetailDialog` to view/charge/apply
 * abonos against an already-open account. No new-admission flow here (that stays on the
 * dedicated Hospitalización board).
 */
export function HospitalizationLookupModal({ on_close }: HospitalizationLookupModalProps): JSX.Element {
  const { t } = useTranslation();
  const [account_id, setAccountId] = useState<string | null>(null);
  const { data, loading } = useQuery<OpenAccountsResponse>(OPEN_ACCOUNTS_QUERY, {
    variables: {
      offset: 0,
      limit: 50,
      kinds: [OPEN_ACCOUNT_KIND_TO_GQL.hospitalization_1, OPEN_ACCOUNT_KIND_TO_GQL.hospitalization_2],
      status: OPEN_ACCOUNT_STATUS_TO_GQL.open,
    },
    fetchPolicy: 'cache-and-network',
  });
  const accounts = data?.openAccounts.items ?? [];

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && on_close()}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('single_station_pos.hospitalization_modal_title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {loading && accounts.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
            ) : null}
            {!loading && accounts.length === 0 ? (
              <p className="text-muted-foreground py-4 text-center text-sm">
                {t('open_accounts.empty')}
              </p>
            ) : null}
            {accounts.map((account) => (
              <Card
                key={account.id}
                role="button"
                tabIndex={0}
                onClick={() => setAccountId(account.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setAccountId(account.id);
                  }
                }}
                className="cursor-pointer border border-border hover:border-primary/50"
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    {account.tutor_name ?? '-'} — {account.pet_name ?? '-'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    {account.folio_number ?? account.id.slice(0, 8)}
                  </span>
                  <span className="font-mono font-semibold">
                    {formatCentsString(account.balance_cents)}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {account_id ? (
        <OpenAccountDetailDialog account_id={account_id} on_close={() => setAccountId(null)} />
      ) : null}
    </>
  );
}
